// Parse an app URL into a structured entity reference for David's UI-context
// snapshot and link interception. Pure (no React/DOM), so it's unit-testable.
//
// The pathname patterns come FROM the manifest (src/config/routeManifest.ts);
// only the query-sheet branch (`/calls?call_id=…&details=1`) is local, because
// a sheet opened inside a page has no manifest entry of its own.

import { matchAppRoute } from './routeManifest'

export type EntityType = 'duty' | 'call' | 'session'

export interface EntityRef {
  type: EntityType
  id: string
  subview?: string
}

const DUTY_SUBVIEWS = new Set(['call', 'playground'])

/**
 * Parse an entity from a full or app-relative URL. Returns null for list/primary
 * routes and anything unrecognized. Mirrors mainRoutes.tsx: `/calls/:id` and
 * `/sessions/:id` are single-segment only; `/duties/:id` allows a `call`/
 * `playground` subview; the regular-user call sheet is `/calls?call_id=…&details=1`.
 */
export function parseEntityFromUrl(href: string): EntityRef | null {
  let url: URL
  try {
    url = new URL(href, 'http://internal')
  } catch {
    return null
  }
  const tab = url.searchParams.get('tab')
  const match = matchAppRoute(url.pathname)

  switch (match?.route.routeId) {
    case 'duty.detail': {
      const dutyId = match.params.dutyId
      if (!dutyId) return null
      // First splat segment, like the switch's segments[2].
      const sub = (match.params['*'] ?? '').split('/')[0]
      return {
        type: 'duty',
        id: dutyId,
        ...(sub && DUTY_SUBVIEWS.has(sub) ? { subview: sub } : {}),
      }
    }
    case 'call.detail': {
      const callId = match.params.callId
      if (!callId) return null
      return { type: 'call', id: callId, ...(tab ? { subview: tab } : {}) }
    }
    case 'session.detail': {
      const sessionId = match.params.sessionId
      if (!sessionId) return null
      return { type: 'session', id: sessionId }
    }
    case 'calls.list': {
      // Regular-user sheet: /calls?call_id=<id>&details=1 — query state, not
      // a pathname route, so it stays local.
      if (url.searchParams.get('details') === '1') {
        const callId = url.searchParams.get('call_id')
        if (callId) {
          return { type: 'call', id: callId, ...(tab ? { subview: tab } : {}) }
        }
      }
      return null
    }
    default:
      return null
  }
}

/**
 * Build the app-relative URL for an entity — the inverse of
 * `parseEntityFromUrl`.
 *
 * The two are round-tripped in the tests, so a new entity type or a changed
 * URL shape cannot be added to one direction and forgotten in the other.
 */
export function entityHref(ref: EntityRef): string {
  switch (ref.type) {
    case 'duty':
      return ref.subview && DUTY_SUBVIEWS.has(ref.subview)
        ? `/duties/${ref.id}/${ref.subview}`
        : `/duties/${ref.id}`

    case 'call': {
      // The sheet form, not `/calls/:id`: it is the link the app itself
      // produces. Both forms sit behind `calls.list` — `/calls` is wrapped in
      // `capabilityRoute('calls.list', …)` in mainRoutes.tsx — so callers must
      // check `ENTITY_CAPABILITY` before offering this link.
      const search = new URLSearchParams({ call_id: ref.id, details: '1' })
      if (ref.subview) search.set('tab', ref.subview)
      return `/calls?${search.toString()}`
    }

    case 'session':
      return `/sessions/${ref.id}`
  }
}

/**
 * The capability the route behind each entity actually reads, for callers that
 * offer a jump to one. Absent means the route carries no capability guard.
 *
 * Only `call` has one: `entityHref` sends it to the *list* route `/calls`,
 * which `mainRoutes.tsx` wraps in `capabilityRoute('calls.list', …)`. The duty,
 * session, and call *detail* routes (`/duties/:id`, `/sessions/:id`,
 * `/calls/:id`) carry no such wrapper, so a jump to those cannot be bounced.
 *
 * Keep this in step with `mainRoutes.tsx`: an entity offered without its
 * capability lands the user on "Your role does not have access to this page."
 */
export const ENTITY_CAPABILITY: Partial<Record<EntityType, string>> = {
  call: 'calls.list',
}

/**
 * Words that name an entity type in a typed query. Plurals and the app's own
 * synonyms are included because the palette is typed fast and from memory —
 * "runs" is what people call sessions.
 */
const ENTITY_KEYWORDS: Record<string, EntityType> = {
  call: 'call',
  calls: 'call',
  duty: 'duty',
  duties: 'duty',
  session: 'session',
  sessions: 'session',
  run: 'session',
  runs: 'session',
}

/**
 * Ids are Supabase UUIDs today. The shape is kept loose so a change of id
 * format doesn't silently stop matching, but long enough that a half-typed
 * token doesn't flash a row that would navigate nowhere.
 */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{7,}$/

/**
 * Parse a typed palette query — `call <id>`, `duty <id>` — into an entity
 * reference. A pasted URL is already one, so it is handed to
 * `parseEntityFromUrl` rather than parsed twice.
 *
 * Returns null for anything that isn't a complete reference, which is what
 * keeps the palette quiet while the user is still typing.
 */
export function parseEntityQuery(input: string): EntityRef | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  if (trimmed.startsWith('/') || trimmed.startsWith('http')) {
    return parseEntityFromUrl(trimmed)
  }

  // `call 9f3a…`, `call: 9f3a…`, `call/9f3a…` — all the ways it gets typed.
  const match = /^([A-Za-z]+)[\s:/]+(\S+)$/.exec(trimmed)
  if (!match) return null

  const type = ENTITY_KEYWORDS[match[1]!.toLowerCase()]
  if (!type) return null

  const id = match[2]!
  return ID_PATTERN.test(id) ? { type, id } : null
}
